import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, LayoutGrid, Rows3, Search, X } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { useDebounced } from "@/hooks/useDirectory";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

export function PageHeader({ eyebrow, title, description, actions, icon: Icon, color }) {
  return (
    <div className="flex flex-col gap-5 pb-8 pt-10 sm:flex-row sm:items-end sm:justify-between lm-fade-up">
      <div className="flex items-start gap-4">
        {Icon && (
          <span className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl sm:flex"
            style={{ backgroundColor: `${color || "#A4247F"}14`, color: color || "#A4247F" }}>
            <Icon className="h-6 w-6" />
          </span>
        )}
        <div>
          {eyebrow && <div className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-[#EC7F13]">{eyebrow}</div>}
          <h1 className="text-3xl font-extrabold sm:text-4xl" data-testid="page-title">{title}</h1>
          {description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testId, className, large = false, autoFocus = false }) {
  const [text, setText] = useState(value);
  const debounced = useDebounced(text, 300);
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (debounced !== value) onChange(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  return (
    <div className={cn("relative flex items-center", className)}>
      <Search className={cn("pointer-events-none absolute text-stone-400", large ? "left-5 h-6 w-6" : "left-3.5 h-4 w-4")} />
      <input
        data-testid={testId}
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full border border-input bg-white text-stone-900 outline-none transition-[border-color,box-shadow] placeholder:text-stone-400 focus:border-primary focus:ring-4 focus:ring-primary/10",
          large ? "h-16 rounded-2xl pl-14 pr-12 text-lg shadow-[0_18px_40px_-24px_rgba(68,26,54,0.45)]" : "h-11 rounded-xl pl-10 pr-10 text-sm",
        )}
      />
      {text && (
        <button type="button" onClick={() => { setText(""); onChange(""); }} data-testid={`${testId}-clear`}
          className={cn("absolute rounded-full p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700", large ? "right-4" : "right-2.5")}
          aria-label="Effacer">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function ViewToggle({ value, onChange }) {
  return (
    <ToggleGroup type="single" value={value} onValueChange={onChange} className="rounded-xl border border-border bg-white p-1">
      <ToggleGroupItem value="cards" aria-label="Vue cartes" data-testid="toggle-view-cards-button" className="h-8 rounded-lg px-2.5 data-[state=on]:bg-accent data-[state=on]:text-primary">
        <LayoutGrid className="h-4 w-4" />
      </ToggleGroupItem>
      <ToggleGroupItem value="table" aria-label="Vue tableau" data-testid="toggle-view-table-button" className="h-8 rounded-lg px-2.5 data-[state=on]:bg-accent data-[state=on]:text-primary">
        <Rows3 className="h-4 w-4" />
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export function Pager({ page, total, pageSize, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-8 flex items-center justify-center gap-3" data-testid="pager">
      <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">
        <ChevronLeft className="h-4 w-4" /> Précédent
      </Button>
      <span className="text-sm text-muted-foreground" data-testid="pager-status">
        Page <b className="text-foreground">{formatNumber(page)}</b> sur {formatNumber(pages)}
      </span>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">
        Suivant <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function InfoRow({ icon: Icon, label, children, testId }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={testId}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />}
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
        <div className="mt-0.5 break-words text-sm text-stone-900">{children}</div>
      </div>
    </div>
  );
}
